import { CommonModule } from '@angular/common'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { EnvironmentProviders, ErrorHandler, inject, NgModule, provideAppInitializer, Provider } from '@angular/core'
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt'
import { RepositoryState } from '@ftr/api-recording-vault'
import {
  API_CONFIGURATION,
  AUTH_CONFIGURATION,
  AUTH_TOKEN_FETCHER,
  STANDALONE_APP_CONFIGURATION,
} from '@ftr/api-shared'
import { CourtSystemsState } from '@ftr/data-court-system'
import {
  BreadcrumbModule,
  ConfirmationModalComponent,
  DialogComponent,
  IllustrationComponent,
  isInDisplayApp,
  KonamiDirective,
  LayoutService,
  OnClickOutsideDirective,
  PageComponent,
  PortalComponent,
  SimpleWindowRefService,
  SizesService,
  TippyComponent,
  ToastModule,
  TrackingService,
} from '@ftr/foundation'
import { FooterComponent, KeyPressState, MfaRequirementsState } from '@ftr/ui-core'
import { DesktopModule } from '@ftr/ui-desktop'
import { FeatureFlagState, LAUNCH_DARKLY_CONFIG, LaunchDarklyModule } from '@ftr/ui-feature-flags'
import { CourtroomState } from '@ftr/ui-location'
import { OrderState } from '@ftr/ui-ordering'
import { UpdateAppToastModule } from '@ftr/ui-playback'
import { AnnotationsState, RealTimeSealingState, RealTimeSessionsState, RealTimeSttState } from '@ftr/ui-rtstt'
import { RealTimeSearchState, SearchManyState, ThisRecordingSearchState } from '@ftr/ui-search'
import {
  AnalyticsService,
  AuthenticationService,
  AuthTokenFetcherService,
  FavoritesState,
  MfaSetupModalComponent,
  OrderAccountState,
  UseConditionsState,
  UserState,
} from '@ftr/ui-user'
import { VocabularyState } from '@ftr/ui-vocab'
import '@js-joda/timezone-10-year-range'
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin'
import { NgxsModule } from '@ngxs/store'
import { JobServiceModule } from '~app/services/job-service/job-service.module'
import { LaunchDarklyAttributeService } from '~app/services/launch-darkly-attribute/launch-darkly-attribute.service'
import { NGXS_OPTIONS } from '~store/ngxs-options'
/*
 * Platform and Environment providers/directives/pipes
 */
import {
  AnnotationTypesState,
  InternalPositionsState,
  PeopleAnnotationsState,
  PeopleProfilesState,
} from '@ftr/api-annotations'
import { DepartmentsServiceModule } from '@ftr/api-core'
import { RealtimePlaybackPageSaveStatusState } from '@ftr/data-realtime-playback'
import { RecordingsFilterState } from '@ftr/data-recording'
import { CasesState, HearingsState } from '@ftr/feature-annotations'
import { ComplexFilterState } from '@ftr/lists'
import { PostLoginNotificationComponent } from '@ftr/ui-audit'
import { ManageCourtroomState } from '@ftr/ui-manage-courtroom'
import { ErrorHandlerService, OBSERVABILITY_CONFIGURATION } from '@ftr/ui-observability'
import { HeaderSizeService, PlaybackState, SharedRecordingState } from '@ftr/ui-playback'
import { RecordingTimelineState } from '@ftr/ui-recording-timeline'
import { ReportFilterState } from '@ftr/ui-reports'
import { UpcomingHearingsListState } from '@ftr/ui-upcoming-hearings-list'
import { AppLoaderComponent } from '@ftr/ui-user'
import { WindowRefService } from '@ftr/window'
import { provideAngularSvgIcon, SvgIconComponent } from 'angular-svg-icon'
import { AppDisplayComponent } from '~app/core/app-display/app-display.component'
import { AppNavigationLoaderComponent } from '~app/core/app-navigation-loader/app-navigation-loader.component'
import { HttpDecodeInterceptor } from '~app/services/http-client/http-decode-interceptor'
import { AppLayoutService } from '~app/services/layout/app-layout.service'
import { environment } from '~environments/environment'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { CoreModule } from './core/core.module'
import { HeaderModule } from './core/header/header.module'
import { ConfigurationService } from './services/configuration/configuration.service'
import { AuthRedirectInterceptor } from './services/http-client/auth-redirect.interceptor'
import { jwtOptionsFactory } from './services/http-client/jwt-options.factory'
import { LoggingInterceptor } from './services/http-client/logging.interceptor'
import { OrderServiceModule } from './services/order-service/order-service.module'

// Application wide providers
const APP_PROVIDERS: (Provider | EnvironmentProviders)[] = [
  { provide: ErrorHandler, useClass: ErrorHandlerService },
  { provide: TrackingService, useClass: AnalyticsService },
  { provide: SimpleWindowRefService, useClass: WindowRefService },
  { provide: SizesService, useClass: HeaderSizeService },
  { provide: LayoutService, useClass: AppLayoutService },
  { provide: LAUNCH_DARKLY_CONFIG, useClass: ConfigurationService },
  { provide: API_CONFIGURATION, useClass: ConfigurationService },
  { provide: AUTH_CONFIGURATION, useClass: ConfigurationService },
  { provide: STANDALONE_APP_CONFIGURATION, useClass: ConfigurationService },
  { provide: OBSERVABILITY_CONFIGURATION, useClass: ConfigurationService },
  { provide: AUTH_TOKEN_FETCHER, useClass: AuthTokenFetcherService },
  // Wire up launch darkly to apply attributes globally from the store
  provideAppInitializer(() => {
    const initializerFn = ((service: LaunchDarklyAttributeService) => () => {
      if (isInDisplayApp()) {
        // Don't apply user based feature flag setup in display app
        return
      }
      service.observeAndApplyUserAttributes()
    })(inject(LaunchDarklyAttributeService))
    return initializerFn()
  }),
  // Wire up analytics service to observe page views and auth events (user login/logout)
  provideAppInitializer(() => {
    const initializerFn = ((service: AnalyticsService) => () => {
      if (isInDisplayApp()) {
        // Don't apply GA setup for display app
        return
      }
      service.observePageViewsAndAuthEvents()
    })(inject(AnalyticsService))
    return initializerFn()
  }),
  provideAngularSvgIcon(),
]

const httpInterceptorProviders = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthRedirectInterceptor,
    multi: true,
  },
  { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: HttpDecodeInterceptor, multi: true },
]

/**
 * `AppModule` is the main entry point into Angular's bootstrap process
 */
@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent],
  imports: [
    CommonModule,
    NgxsModule.forRoot(
      [
        UserState,
        ThisRecordingSearchState,
        SearchManyState,
        PlaybackState,
        CourtSystemsState,
        VocabularyState,
        RepositoryState,
        OrderState,
        OrderAccountState,
        CourtroomState,
        FeatureFlagState,
        RealTimeSttState,
        RealTimeSearchState,
        UseConditionsState,
        RecordingsFilterState,
        MfaRequirementsState,
        SharedRecordingState,
        AnnotationsState,
        RealTimeSessionsState,
        RealTimeSealingState,
        KeyPressState,
        CasesState,
        HearingsState,
        RecordingTimelineState,
        FavoritesState,
        ComplexFilterState,
        UpcomingHearingsListState,
        ReportFilterState,
        ManageCourtroomState,
        RealtimePlaybackPageSaveStatusState,
        PeopleAnnotationsState,
        AnnotationTypesState,
        InternalPositionsState,
        PeopleProfilesState,
      ],
      NGXS_OPTIONS,
    ),
    AppDisplayComponent,
    AppLoaderComponent,
    HeaderModule,
    AppRoutingModule,
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        deps: [AuthenticationService, ConfigurationService],
      },
    }),
    CoreModule.forRoot(),
    DepartmentsServiceModule,
    OrderServiceModule.forRoot(),
    JobServiceModule.forRoot(),
    AppRoutingModule,
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
      name: 'FTR-WEB',
      // These are super noisy and polling, essentially making the devtools unusable
      actionsBlacklist: ['\\[Annotations\\] Set Live LocalTime', '\\[Playback\\] Reevaluate on record state'],
    }),
    BreadcrumbModule,
    ToastModule,
    DesktopModule,
    UpdateAppToastModule,
    MfaSetupModalComponent,
    PostLoginNotificationComponent,
    PortalComponent,
    PageComponent,
    LaunchDarklyModule.forRoot(),
    DialogComponent,
    ConfirmationModalComponent,
    FooterComponent,
    KonamiDirective,
    IllustrationComponent,
    TippyComponent,
    OnClickOutsideDirective,
    AppNavigationLoaderComponent,
    SvgIconComponent,
  ],
  providers: [APP_PROVIDERS, httpInterceptorProviders, provideHttpClient(withInterceptorsFromDi())],
})
export class AppModule {}
